/**
 * Master Stat Calculations Helper
 * 
 * This module provides utility functions for calculating Master Stat values
 * and handling Master Stat changes in the Project Home grid.
 */

import { getMonthsBetweenDates } from './utils';
import { debugLogger } from './debugLogger';
import { OPERATORS } from './constants';

/**
 * Generates a GL key for a Master Stat
 * @param {String} masterStatName - Name of the Master Stat
 * @param {String} entityId - Entity ID
 * @param {String} departmentId - Department ID
 * @returns {String} Generated GL key in format MASTER-{entityId}-{departmentId}-{NAME}
 */
export const generateMasterStatGlKey = (masterStatName, entityId, departmentId) => {
  const normalizedName = masterStatName
    .toUpperCase()
    .trim()
    .replace(/\s+/g, '-');
  
  return `MASTER-${entityId}-${departmentId}-${normalizedName}`;
};

/**
 * Validates Master Stat hierarchy rules
 * @param {Array} childStatGlKeys - Array of child stat GL keys
 * @param {Object} treeData - Hierarchical tree data structure
 * @returns {Object} Validation result with valid flag and errors array
 */
export const validateMasterStatHierarchy = (childStatGlKeys, treeData) => {
  const errors = [];
  
  if (!childStatGlKeys || childStatGlKeys.length === 0) {
    errors.push('Master Stat must have at least one child stat');
    return { valid: false, errors };
  }
  
  // Extract department IDs from GL keys
  const departments = new Set();
  const invalidStats = [];
  
  childStatGlKeys.forEach(glKey => {
    const parts = glKey.split('-');
    if (parts.length >= 3) {
      const entityId = parts[0];
      const departmentId = parts[1];
      const accountId = parts[2];
      
      departments.add(`${entityId}-${departmentId}`);
      
      // Check if account exists and is a Stat type
      try {
        const account = treeData?.entities?.[entityId]?.departments?.[departmentId]?.accounts?.[accountId];
        if (!account) {
          invalidStats.push(`${glKey}: Account not found`);
        } else if (account.acctType !== 'Stat') {
          invalidStats.push(`${glKey} is not a Stat account type (found: ${account.acctType})`);
        }
      } catch (e) {
        invalidStats.push(`${glKey}: Error accessing account data`);
      }
    }
  });
  
  // Check if all child stats are from the same department
  if (departments.size > 1) {
    errors.push('All child stats must be from the same department');
  }
  
  // Add invalid stat errors
  errors.push(...invalidStats);
  
  return {
    valid: errors.length === 0,
    errors
  };
};

/**
 * Distributes a Master Stat change proportionally to child stats
 * @param {Object} masterStat - Master Stat definition
 * @param {Number} newMasterValue - New total value for Master Stat
 * @param {Number} oldMasterValue - Old total value for Master Stat
 * @param {Array} gridRows - Current grid row data
 * @param {String} month - Month string
 * @returns {Array} Array of child stat adjustments
 */
export const distributeMasterStatChange = (masterStat, newMasterValue, oldMasterValue, gridRows, month) => {
  debugLogger.log('MASTER_STAT', 'Distributing master stat change', {
    masterStatGlKey: masterStat?.gl_key,
    masterStatName: masterStat?.master_stat_name,
    month,
    oldValue: oldMasterValue,
    newValue: newMasterValue,
    delta: newMasterValue - oldMasterValue,
    childStatsCount: masterStat?.child_stats?.length
  });
  
  const adjustments = [];
  
  if (!masterStat || !gridRows || !month) {
    debugLogger.warn('MASTER_STAT', 'Missing required data for distribution', {
      hasMasterStat: !!masterStat,
      hasGridRows: !!gridRows,
      hasMonth: !!month
    });
    return adjustments;
  }
  
  // Get current values for all child stats
  const childValues = masterStat.child_stats.map(childGlKey => {
    const childRow = gridRows.find(r => r.glKey === childGlKey);
    const value = childRow?.[month] || 0;
    return { gl_key: childGlKey, value, found: !!childRow };
  }).filter(child => child.found);
  
  // Calculate total current value
  const currentTotal = childValues.reduce((sum, child) => sum + child.value, 0);
  
  // Calculate the ratio to apply to each child.
  // When all children are zero we fall back to even distribution so the change
  // is not silently dropped (matches the behaviour in calculateMasterStatChangeAllMonths).
  const delta = newMasterValue - oldMasterValue;
  
  if (currentTotal !== 0) {
    const ratio = newMasterValue / currentTotal;
    childValues.forEach(child => {
      const newValue = Math.round(child.value * ratio * 100) / 100;
      const changeAmount = Math.round((newValue - child.value) * 100) / 100;
      adjustments.push({
        gl_key: child.gl_key,
        old_value: child.value,
        new_value: newValue,
        change_amount: changeAmount
      });
    });
  } else {
    // All children are zero — distribute the delta evenly
    const numChildren = childValues.length;
    const changePerChild = numChildren > 0 ? Math.round((delta / numChildren) * 100) / 100 : 0;
    childValues.forEach(child => {
      adjustments.push({
        gl_key: child.gl_key,
        old_value: 0,
        new_value: changePerChild,
        change_amount: changePerChild
      });
    });
  }
  
  return adjustments;
};

/**
 * Recalculates a Master Stat value for a single month
 * @param {Object} masterStat - Master Stat definition
 * @param {String} month - Month string
 * @param {Array} gridRows - Current grid row data
 * @param {Array} appliedChanges - Array of changes applied to the project
 * @returns {Number} Recalculated Master Stat value
 */
export const recalculateMasterStatValue = (masterStat, month, gridRows, appliedChanges) => {
  if (!masterStat || !month || !gridRows) {
    return 0;
  }
  
  // Sum the current values of all child stats for this month
  const total = masterStat.child_stats.reduce((sum, childGlKey) => {
    const childRow = gridRows.find(r => r.glKey === childGlKey);
    
    if (!childRow) {
      return sum;
    }
    
    // Start with base value
    let childValue = childRow[month] || 0;
    
    // Apply changes for this child in this month
    if (appliedChanges && Array.isArray(appliedChanges)) {
      appliedChanges.forEach(change => {
        // Check if this change applies to this child stat
        const appliesToThisChild = change.selected_accounts && 
          change.selected_accounts.includes(childGlKey);
        
        if (appliesToThisChild) {
          // Apply the change based on function_operator
          if (change.function_operator === OPERATORS.ABSOLUTE) {
            childValue += (change.operator_value || 0);
          } else if (change.function_operator === OPERATORS.PERCENTAGE) {
            childValue += childValue * ((change.operator_value || 0) / 100);
          }
        } else if (change.change_label) {
          // Legacy behavior: look for pre-applied change in gridRow
          const changeFieldName = `${change.change_label} ${month}`;
          const changeValue = childRow[changeFieldName] || 0;
          childValue += changeValue;
        }
      });
    }
    
    return sum + childValue;
  }, 0);
  
  return total;
};

/**
 * Calculates Master Stat change values for all months and distributes proportionally to child stats
 * @param {Object} change - Change object with function_operator, operator_value, date_range
 * @param {Object} masterStat - Master Stat definition with gl_key, child_stats array
 * @param {Array} gridRows - Current grid row data
 * @returns {Object} Object with month keys, each containing array of child stat adjustments
 */
export const calculateMasterStatChangeAllMonths = (change, masterStat, gridRows) => {
  const monthlyAdjustments = {};
  
  if (!change || !masterStat || !gridRows) {
    return monthlyAdjustments;
  }
  
  // Get date range for the change
  const dateRange = getMonthsBetweenDates(
    change.date_range[0],
    change.date_range[1]
  ) || [];
  
  const numMonths = dateRange.length;
  const numChildren = masterStat.child_stats.length;
  
  // For absoluteValue operator, operator_value is the change amount PER MONTH for the Master Stat
  // This needs to be distributed across all children PROPORTIONALLY based on their base period values
  if (change.function_operator === OPERATORS.ABSOLUTE) {
    const changePerMonth = Number(change.operator_value) || 0;
    
    // Apply proportional distribution for each month
    dateRange.forEach(month => {
      // Get current base period values for all child stats in this month
      const childValues = masterStat.child_stats.map(childGlKey => {
        const childRow = gridRows.find(r => r.glKey === childGlKey);
        const value = childRow?.[month] || 0;
        return { gl_key: childGlKey, value };
      });
      
      // Calculate total base period value for this month
      const totalBasePeriod = childValues.reduce((sum, child) => sum + child.value, 0);
      
      // Distribute change proportionally based on each child's share of the total
      const adjustments = childValues.map(child => {
        let changeAmount = 0;
        
        if (totalBasePeriod !== 0) {
          // Proportional distribution: change × (child value / total value)
          const proportion = child.value / totalBasePeriod;
          changeAmount = changePerMonth * proportion;
        } else {
          // If all children are 0, distribute evenly
          changeAmount = numChildren > 0 ? changePerMonth / numChildren : 0;
        }
        
        return {
          gl_key: child.gl_key,
          old_value: child.value,
          new_value: child.value + changeAmount,
          change_amount: changeAmount
        };
      });
      
      monthlyAdjustments[month] = adjustments;
    });
    
    return monthlyAdjustments;
  }
  
  // For percentage and other operators, calculate based on current values
  dateRange.forEach(month => {
    const adjustments = [];
    
    // Get current values for all child stats in this month
    const childValues = masterStat.child_stats.map(childGlKey => {
      const childRow = gridRows.find(r => r.glKey === childGlKey);
      const value = childRow?.[month] || 0;
      return { gl_key: childGlKey, value };
    });
    
    // Calculate total current value
    const currentTotal = childValues.reduce((sum, child) => sum + child.value, 0);
    
    // Calculate new total based on change operator
    let newTotal = currentTotal;
    
    if (change.function_operator === OPERATORS.PERCENTAGE) {
      // Percentage change: multiply by (1 + percentage/100)
      const multiplier = 1 + (change.operator_value / 100);
      newTotal = currentTotal * multiplier;
    }
    
    // Calculate the change amount (difference between new and old)
    const totalChangeAmount = newTotal - currentTotal;
    
    // Distribute the change
    if (currentTotal !== 0) {
      // If there are existing values, distribute proportionally
      const ratio = newTotal / currentTotal;
      
      childValues.forEach(child => {
        const newValue = Math.round(child.value * ratio * 100) / 100; // Round to 2 decimals
        const changeAmount = Math.round((newValue - child.value) * 100) / 100; // Round to 2 decimals
        
        adjustments.push({
          gl_key: child.gl_key,
          old_value: child.value,
          new_value: newValue,
          change_amount: changeAmount
        });
      });
    } else {
      // If all child stats are 0, distribute evenly
      const numChildren = childValues.length;
      const changePerChild = numChildren > 0 
        ? Math.round((totalChangeAmount / numChildren) * 100) / 100 
        : 0;
      
      childValues.forEach(child => {
        adjustments.push({
          gl_key: child.gl_key,
          old_value: 0,
          new_value: changePerChild,
          change_amount: changePerChild
        });
      });
    }
    
    monthlyAdjustments[month] = adjustments;
  });
  
  return monthlyAdjustments;
};

/**
 * Recalculates Master Stat values for all months based on current child stat values
 * Used when child stats are changed and Master Stat needs to reflect the sum
 * @param {Object} masterStat - Master Stat definition
 * @param {Array} dateRange - Array of month strings
 * @param {Array} allRows - All grid rows
 * @param {Array} allChanges - All changes applied to the project
 * @returns {Object} Object with month keys and recalculated Master Stat values
 */
export const recalculateMasterStatAllMonths = (masterStat, dateRange, allRows, allChanges) => {
  const recalculatedValues = {};
  
  if (!masterStat || !dateRange || !allRows) {
    return recalculatedValues;
  }
  
  dateRange.forEach(month => {
    // Sum the current values of all child stats for this month
    const total = masterStat.child_stats.reduce((sum, childGlKey) => {
      const childRow = allRows.find(r => r.glKey === childGlKey);
      
      if (!childRow) {
        return sum;
      }
      
      // Start with base value
      let childValue = childRow[month] || 0;
      
      // Apply changes for this child in this month
      if (allChanges && Array.isArray(allChanges)) {
        allChanges.forEach(change => {
          // Check if this change applies to this child stat
          const appliesToThisChild = change.selected_accounts && 
            change.selected_accounts.includes(childGlKey);
          
          if (appliesToThisChild) {
            // Apply the change based on function_operator
            if (change.function_operator === OPERATORS.ABSOLUTE) {
              childValue += (change.operator_value || 0);
            } else if (change.function_operator === OPERATORS.PERCENTAGE) {
              childValue += childValue * ((change.operator_value || 0) / 100);
            }
          } else if (change.change_label && childRow.changes && Array.isArray(childRow.changes)) {
            // Legacy behavior: look for pre-applied change in gridRow if this change is associated with this row
            if (childRow.changes.includes(change.id)) {
              const changeFieldName = `${change.change_label} ${month}`;
              const changeValue = childRow[changeFieldName] || 0;
              childValue += changeValue;
            }
          }
        });
      }
      
      return sum + childValue;
    }, 0);
    
    recalculatedValues[month] = total;
  });
  
  return recalculatedValues;
};

/**
 * Gets child stat drivers from a Master Stat
 * @param {Object} masterStat - Master Stat definition
 * @param {Array} allRows - All grid rows
 * @returns {Array} Array of child stat GL keys that are drivers
 */
export const getChildStatDrivers = (masterStat, allRows) => {
  if (!masterStat || !masterStat.child_stats || !allRows) {
    return [];
  }
  
  return masterStat.child_stats.filter(childGlKey => {
    // Find the child row by glKey or by constructing it from entity_id-department_id-account_id
    const childRow = allRows.find(r => {
      if (r.glKey === childGlKey) {
        return true;
      }
      // Construct GL key from entity_id, department_id, account_id if available
      if (r.entity_id && r.department_id && r.account_id) {
        const constructedGlKey = `${r.entity_id}-${r.department_id}-${r.account_id}`;
        return constructedGlKey === childGlKey;
      }
      return false;
    });
    
    // Check if this child stat is used as a driver by any other account
    if (!childRow || !childRow.account_name) {
      return false;
    }
    
    // Check if any other account references this stat as a driver
    return allRows.some(row => {
      return row.driver && (
        row.driver.includes(childRow.account_name) ||
        row.driver.includes(childGlKey)
      );
    });
  });
};

/**
 * Gets accounts driven by a stat driver
 * @param {String} driverGlKey - GL key of the stat driver
 * @param {Array} allRows - All grid rows
 * @returns {Array} Array of driven account objects
 */
export const getDrivenAccountsByStatDriver = (driverGlKey, allRows) => {
  if (!driverGlKey || !allRows) {
    return [];
  }
  
  // Find the driver row by glKey or by constructing it from entity_id-department_id-account_id
  const driverRow = allRows.find(r => {
    if (r.glKey === driverGlKey) {
      return true;
    }
    // Construct GL key from entity_id, department_id, account_id if available
    if (r.entity_id && r.department_id && r.account_id) {
      const constructedGlKey = `${r.entity_id}-${r.department_id}-${r.account_id}`;
      return constructedGlKey === driverGlKey;
    }
    return false;
  });
  
  if (!driverRow) {
    return [];
  }
  
  const driverName = driverRow.account_name || driverRow.accountName;
  
  if (!driverName) {
    return [];
  }
  
  // Find all accounts that reference this driver (by name or GL key)
  return allRows.filter(row => {
    return row.driver && (
      row.driver.includes(driverName) ||
      row.driver.includes(driverGlKey)
    );
  });
};

/**
 * Checks if a Master Stat is used as a driver by any account
 * @param {Object} masterStat - Master Stat object with gl_key and master_stat_name
 * @param {Array} allRows - All grid rows
 * @returns {Boolean} True if the Master Stat is used as a driver
 */
export const isMasterStatDriver = (masterStat, allRows) => {
  if (!masterStat || !allRows) {
    return false;
  }
  
  const masterStatName = masterStat.master_stat_name;
  const masterStatGlKey = masterStat.gl_key;
  
  if (!masterStatName && !masterStatGlKey) {
    return false;
  }
  
  // Check if any account references this Master Stat as a driver
  return allRows.some(row => {
    return row.driver && (
      (masterStatName && row.driver.includes(masterStatName)) ||
      (masterStatGlKey && row.driver.includes(masterStatGlKey))
    );
  });
};
